import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, take, filter, distinctUntilChanged } from 'rxjs/operators';
import { AuthService, AuthStatus } from '../../auth/services/auth.service';
import { TandemUser } from '../../auth/models/tandem-user';
import { Release, isNewerVersion } from '../../admin/models/release';

export interface WhatsNewState {
  hasNewRelease: boolean;
  latestRelease: Release | null;
  unseenReleases: Release[];
  lastSeenVersion: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class WhatsNewService {
  private state = new BehaviorSubject<WhatsNewState>({
    hasNewRelease: false,
    latestRelease: null,
    unseenReleases: [],
    lastSeenVersion: null
  });

  public state$ = this.state.asObservable();
  public hasNewRelease$ = this.state$.pipe(map(s => s.hasNewRelease), distinctUntilChanged());

  private currentUser: TandemUser | null = null;

  constructor(
    private afs: AngularFirestore,
    private authService: AuthService
  ) {
    this.initializeWatcher();
  }

  private initializeWatcher(): void {
    combineLatest([
      this.authService.$authState,
      this.authService.$customClaims
    ]).pipe(
      filter(([auth]) => auth.authInitialized && auth.authStatus === AuthStatus.VerifiedAndDocExists),
      switchMap(([auth, claims]) => {
        this.currentUser = auth.user || null;

        // TEMPORARY: only show What's New to admins while validating the feature.
        // Remove this check to roll the banner out to all users.
        if (!claims['isAdmin']) {
          return of<[string | null, Release[]]>([null, []]);
        }

        const lastSeenVersion = auth.user?.lastSeenReleaseVersion || null;

        return combineLatest([
          of(lastSeenVersion),
          this.getPublishedReleases()
        ]);
      })
    ).subscribe(([lastSeenVersion, releases]) => {
      const latestRelease = releases.length > 0 ? releases[0] : null;
      const hasNewRelease = latestRelease ? isNewerVersion(latestRelease.version, lastSeenVersion) : false;
      
      const unseenReleases = lastSeenVersion 
        ? releases.filter(r => isNewerVersion(r.version, lastSeenVersion))
        : releases;

      this.state.next({
        hasNewRelease,
        latestRelease,
        unseenReleases,
        lastSeenVersion
      });
    });
  }

  private getPublishedReleases(): Observable<Release[]> {
    return this.afs.collection<Release>(
      'releases',
      ref => ref
        .where('status', '==', 'published')
        .orderBy('publishedAt', 'desc')
    ).snapshotChanges().pipe(
      map(changes => changes.map(a => {
        const data = a.payload.doc.data() as Release;
        data.id = a.payload.doc.id;
        return data;
      }))
    );
  }

  async acknowledgeRelease(version: string): Promise<void> {
    if (!this.currentUser?.uid) return;

    await this.afs.collection('users').doc(this.currentUser.uid).update({
      lastSeenReleaseVersion: version
    });

    const currentState = this.state.value;
    this.state.next({
      ...currentState,
      hasNewRelease: false,
      lastSeenVersion: version,
      unseenReleases: []
    });
  }

  async acknowledgeLatestRelease(): Promise<void> {
    const latestRelease = this.state.value.latestRelease;
    if (latestRelease) {
      await this.acknowledgeRelease(latestRelease.version);
    }
  }

  getUnseenReleases(): Observable<Release[]> {
    return this.state$.pipe(map(s => s.unseenReleases));
  }

  getLatestRelease(): Observable<Release | null> {
    return this.state$.pipe(map(s => s.latestRelease));
  }

  checkForNewRelease(): Observable<boolean> {
    return this.hasNewRelease$;
  }
}
